// dashboard/src/api/push.ts
import api from './client';
import { useAuthStore } from '../store/auth';
import { API_FETCH_URL } from './base';

const BASE_URL = API_FETCH_URL;

export interface PushApp {
  id: string;
  name: string;
  platform: string;
  bundle_id: string;
  status: string;
  push_firebase_account_id: number | null;
  push_firebase_project_id: string | null;
  firebase_account_name: string | null;
}

export interface PushTemplate {
  id: string;
  name: string;
  fallback_locale: string;
  default_deeplink: string | null;
  variant_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface PushVariant {
  id: string;
  template_id: string;
  locale: string;
  title: string;
  body: string;
  image_r2_key: string | null;
  deeplink: string | null;
}

export interface PushSchedule {
  id: string;
  app_id?: string | null;
  template_id: string;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'deleted';
  kind: 'once' | 'daily' | 'weekly';
  send_at_local_minutes: number;
  once_date: string | null;
  weekly_days: string | null;
  starts_on: string | null;
  ends_on: string | null;
  target_countries: string | null;
  target_locales: string | null;
  target_min_app_version: string | null;
  target_max_app_version: string | null;
  exclude_blocked: number;
  app_name?: string;
  template_name?: string;
  pwa_id?: string | null;
  tracker_pwa_id?: string | null;
  target_type?: 'app' | 'pwa' | 'tracker_pwa';
  target_name?: string | null;
}

export interface PushDelivery {
  id: string;
  device_id: string;
  bundle_id: string;
  locale_used: string;
  fcm_message_id: string | null;
  status: string;
  error: string | null;
  sent_at: string;
  occurrence_date: string;
}

export interface PushDeliveryCounts {
  sent: number;
  failed: number;
  token_invalid: number;
}

// Apps
export const listPushApps = () => api.get<{ apps: PushApp[] }>('/admin/push/apps');
export const setAppPush = (id: string, body: { firebase_account_id: number | null; firebase_project_id: string | null }) =>
  api.patch<null>(`/admin/push/apps/${id}/push`, body);
export const testAppPush = (id: string, device_id: string) =>
  api.post<{ result: unknown }>(`/admin/push/apps/${id}/push/test`, { device_id });

export interface PushTestDevice {
  device_id: string;
  locale: string | null;
  tz_offset_min: number | null;
  push_channel: string;
  push_token_updated_at: string | null;
  push_unregistered_at: string | null;
}

export const generateTestLink = (id: string) =>
  api.post<{ token: string; url: string }>(`/admin/push/apps/${id}/test-link`, {});
export const listTestDevices = (id: string) =>
  api.get<{ devices: PushTestDevice[] }>(`/admin/push/apps/${id}/test-devices`);

// Push Test — the developer-role section (see /admin/push-test on the worker).
export type PushTestChannel = 'auto' | 'native' | 'web';

export interface PushTestResult {
  result: unknown;
  resolved_from: 'token' | 'device_id' | 'raw_token';
}

export const listPushTestApps = () => api.get<{ apps: PushApp[] }>('/admin/push-test/apps');

export const sendTestPush = (
  id: string,
  body: { target: string; title?: string; body?: string; channel?: PushTestChannel },
) => api.post<PushTestResult>(`/admin/push-test/apps/${id}/send`, body);

export const testRun = (id: string) =>
  api.post<{ sent: number; failed: number; token_invalid: number; skipped: number }>(`/admin/push/schedules/${id}/test-run`, {});

// Templates
export const listTemplates = () => api.get<{ templates: PushTemplate[] }>('/admin/push/templates');
export const getTemplate = (id: string) => api.get<{ template: PushTemplate; variants: PushVariant[] }>(`/admin/push/templates/${id}`);
export const createTemplate = (body: { name: string; fallback_locale?: string; default_deeplink?: string }) =>
  api.post<{ id: string }>('/admin/push/templates', body);
export const updateTemplate = (id: string, body: Partial<PushTemplate>) =>
  api.patch<null>(`/admin/push/templates/${id}`, body);
export const deleteTemplate = (id: string) => api.delete<null>(`/admin/push/templates/${id}`);
export const putVariant = (id: string, locale: string, body: { title: string; body: string; image_r2_key?: string | null; deeplink?: string | null }) =>
  api.put<null>(`/admin/push/templates/${id}/variants/${locale}`, body);
export const deleteVariant = (id: string, locale: string) =>
  api.delete<null>(`/admin/push/templates/${id}/variants/${locale}`);

// Image upload — multipart, bypass JSON helper.
export async function uploadVariantImage(id: string, locale: string, file: File): Promise<{ image_r2_key: string }> {
  const form = new FormData();
  form.append('file', file);
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${BASE_URL}/admin/push/templates/${id}/variants/${locale}/image`, {
    method: 'POST',
    body: form,
    headers,
  });
  const json = await res.json();
  if (!res.ok || !json?.success) throw new Error(json?.error || `Upload failed ${res.status}`);
  return json.data;
}

// Schedules
export const listSchedules = () => api.get<{ schedules: PushSchedule[] }>('/admin/push/schedules');
export const getSchedule = (id: string) => api.get<{ schedule: PushSchedule }>(`/admin/push/schedules/${id}`);
export const createSchedule = (body: Partial<PushSchedule>) =>
  api.post<{ id: string }>('/admin/push/schedules', body);
export const updateSchedule = (id: string, body: Partial<PushSchedule>) =>
  api.patch<null>(`/admin/push/schedules/${id}`, body);
export const deleteSchedule = (id: string) => api.delete<null>(`/admin/push/schedules/${id}`);
export const previewAudience = (id: string) =>
  api.post<{ audience: number }>(`/admin/push/schedules/${id}/preview`, {});
export const sendNow = (id: string) =>
  api.post<{ sent: number; failed: number; token_invalid: number; skipped: number }>(`/admin/push/schedules/${id}/send-now`, {});
export const listDeliveries = (id: string, since?: string) => {
  const q = since ? `?since=${encodeURIComponent(since)}` : '';
  return api.get<{ deliveries: PushDelivery[]; counts: PushDeliveryCounts }>(`/admin/push/schedules/${id}/deliveries${q}`);
};

// Broadcast — one-off send to every live device of one target (app or PWA).
export interface BroadcastResult {
  sent: number;
  failed: number;
  token_invalid: number;
  skipped: number;
  audience: number;
  schedule_id: string;
}
/** Exactly one of the three ids, same rule as a schedule's target. */
export type BroadcastTarget =
  | { app_id: string; pwa_id?: never; tracker_pwa_id?: never }
  | { pwa_id: string; app_id?: never; tracker_pwa_id?: never }
  | { tracker_pwa_id: string; app_id?: never; pwa_id?: never };

export const broadcastAudience = (target: BroadcastTarget, include_test = false) => {
  const [key, value] = Object.entries(target).find(([, v]) => !!v) ?? ['app_id', ''];
  return api.get<{ audience: number; max: number }>(
    `/admin/push/broadcast/audience?${key}=${encodeURIComponent(String(value))}${include_test ? '&include_test=1' : ''}`,
  );
};
export const sendBroadcast = (body: BroadcastTarget & { title: string; body: string; include_test?: boolean }) =>
  api.post<BroadcastResult>('/admin/push/broadcast', body);

// Analytics
export interface PushAnalyticsRow {
  sent: number;
  failed: number;
  token_invalid: number;
  total: number;
  deliveryRate: number;
}
export interface PushAnalyticsDay {
  date: string;
  sent: number;
  failed: number;
  token_invalid: number;
}
export interface PushAnalyticsCampaign extends PushAnalyticsRow {
  schedule_id: string;
  name: string;
  app_name: string;
}
export interface PushAnalyticsApp extends PushAnalyticsRow {
  app_id: string;
  app_name: string;
}
export interface PushAnalyticsLocale extends PushAnalyticsRow {
  locale: string;
}
export interface PushAnalytics {
  summary: PushAnalyticsRow & { campaigns: number; apps: number };
  byDay: PushAnalyticsDay[];
  byCampaign: PushAnalyticsCampaign[];
  byApp: PushAnalyticsApp[];
  byLocale: PushAnalyticsLocale[];
}

// app_id='all' is the server-side contract for "no app filter" (passed verbatim).
export const getPushAnalytics = (params: { from: string; to: string; app_id: string }) => {
  const q = new URLSearchParams(params);
  return api.get<PushAnalytics>(`/admin/push/analytics?${q.toString()}`);
};
